import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { http } from '@pendo/http';
import { i18n_t as $t } from '@/utils/i18n';

export const DEFAULT_APPLICATION_ROLE = 'predefined_1';

export const VIEWER_ONLY_FILTER = 'viewer_only';

export function isViewerOnlyUser (user, appIdsToCheck = null) {
    const applicationRoles = user?.applicationRoles || {};
    const appsToCheck = appIdsToCheck?.length
        ? appIdsToCheck
        : Object.keys(applicationRoles);

    if (!appsToCheck.length) {
        return false;
    }

    return appsToCheck.every((appId) => {
        const roleIds = applicationRoles[appId];

        return roleIds?.length === 1 && roleIds[0] === DEFAULT_APPLICATION_ROLE;
    });
}

export const REPLAY_USER_ROLE = 'predefined_9';

export const SUBSCRIPTION_USER_ROLE = 'predefined_sub_2';

export const SUBSCRIPTION_ADMIN_ROLE = 'predefined_sub_1';

export const SHARE_SEGMENTS_ROLE = 'predefined_sub_3';

export const VERIFY_SEGMENTS_ROLE = 'segment_approver_sub_role';

export const SHARE_REPORTS_ROLE = 'predefined_sub_4';

export const MANAGE_PRODUCT_AREAS_SUB_ROLE = 'product_area_manager_sub_role';

export const MANAGE_GUIDE_THEMES_SUB_ROLE = 'guide_theme_manager_sub_role';

export const MANAGE_INCLUDE_EXCLUDE_LISTS_SUB_ROLE = 'manage_blacklist_sub_role';

export const VIEW_INCLUDE_EXCLUDE_LISTS_SUB_ROLE = 'view_blacklist_sub_role';

export const AI_AGENT_ADMIN_ROLE = 'predefined_8';

export const JOURNEY_VIEWER_ROLE = 'predefined_10';

export const JOURNEY_CONTENT_EDITOR_ROLE = 'predefined_11';

export const JOURNEY_CREATOR_ROLE = 'predefined_12';

export const JOURNEY_PUBLISHER_ROLE = 'predefined_13';

export const EMAIL_VIEWER_ROLE = 'predefined_14';

export const EMAIL_CONTENT_EDITOR_ROLE = 'predefined_15';

export const EMAIL_CREATOR_ROLE = 'predefined_16';

export const EMAIL_PUBLISHER_ROLE = 'predefined_17';

export const JOURNEY_ROLES = [JOURNEY_VIEWER_ROLE, JOURNEY_CONTENT_EDITOR_ROLE, JOURNEY_CREATOR_ROLE, JOURNEY_PUBLISHER_ROLE];

export const EMAIL_ROLES = [EMAIL_VIEWER_ROLE, EMAIL_CONTENT_EDITOR_ROLE, EMAIL_CREATOR_ROLE, EMAIL_PUBLISHER_ROLE];

// Predefined roles hidden from the user-management role pickers. Existing assignments still hold.
export const HIDDEN_ROLES = [JOURNEY_VIEWER_ROLE, EMAIL_VIEWER_ROLE, JOURNEY_CONTENT_EDITOR_ROLE];

export const TAGGING_EDITOR_ROLE = 'predefined_4';

export const SUPER_ROLE = 'super';

export const GLOBAL_ACCESS_ROLE = 'global_access';

export const OPS_ROLE = 'ops';

export const SUPPORT_ROLE = 'support';

export const ADOPT_MANAGER_ROLE = 'adopt_manager_sub_role';

export const VOC_VIEWER_ROLE = 'voc_viewer_role';

export const VOC_MANAGER_ROLE = 'predefined_sub_6';

export const VOC_CONTRIBUTOR_ROLE = 'predefined_sub_7';

export const ROADMAP_VIEWER_ROLE = 'roadmap_viewer_sub_role';

export const ROADMAP_CREATOR_ROLE = 'roadmap_creator_sub_role';

export const ROADMAP_PUBLISHER_ROLE = 'roadmap_publisher_sub_role';

export const COMMAND_CENTER_VIEW_ROLE = 'command_center_view_sub_role';

export const RESOURCE_CENTER_PERMISSION_SETS = ['7', '8', '9', '24'];

// Translation key namespaces use camelCase role identifiers (e.g. `appViewer`) rather than
// the backend role IDs (e.g. `predefined_1`). The `i18n_t` key resolver only splits on dots
// flanked by letters, so a digit-adjacent dot like `predefined_1.name` would not split and
// would fail lookup.
export const ROLE_MAP = {
    default: {
        get name () { return $t('rolesAndPermissions.defaultPanelTitle'); },
        get description () { return $t('rolesAndPermissions.defaultDescription'); },
    },
    sunrise_default: {
        get name () { return $t('rolesAndPermissions.sunriseDefaultName'); },
        get description () { return $t('rolesAndPermissions.sunriseDefaultDescription'); },
    },
    predefined_1: {
        get name () { return $t('rolesAndPermissions.appViewerName'); },
        get description () {
            return [
                $t('rolesAndPermissions.appViewerDescription1'),
                $t('rolesAndPermissions.appViewerDescription2'),
            ];
        },
    },
    predefined_2: {
        get name () { return $t('rolesAndPermissions.guidePublisherName'); },
        get description () {
            return [
                $t('rolesAndPermissions.guidePublisherDescription1'),
                $t('rolesAndPermissions.guidePublisherDescription2'),
            ];
        },
    },
    predefined_3: {
        get name () { return $t('rolesAndPermissions.guideCreatorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.guideCreatorDescription1'),
                $t('rolesAndPermissions.guideCreatorDescription2'),
                $t('rolesAndPermissions.guideCreatorDescription3'),
                $t('rolesAndPermissions.guideCreatorDescription4'),
            ];
        },
    },
    predefined_4: {
        get name () { return $t('rolesAndPermissions.tagEditorName'); },
        get description () {
            return [$t('rolesAndPermissions.tagEditorDescription1')];
        },
    },
    predefined_5: {
        get name () { return $t('rolesAndPermissions.guideContentEditorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.guideContentEditorDescription1'),
                $t('rolesAndPermissions.guideContentEditorDescription2'),
                $t('rolesAndPermissions.guideContentEditorDescription3'),
                $t('rolesAndPermissions.guideContentEditorDescription4'),
            ];
        },
    },
    predefined_6: {
        get name () { return $t('rolesAndPermissions.resourceCenterAuthorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.resourceCenterAuthorDescription1'),
                $t('rolesAndPermissions.resourceCenterAuthorDescription2'),
            ];
        },
    },
    predefined_7: {
        get name () { return $t('rolesAndPermissions.resourceCenterPublisherName'); },
        get description () {
            return [
                $t('rolesAndPermissions.resourceCenterPublisherDescription1'),
                $t('rolesAndPermissions.resourceCenterPublisherDescription2'),
            ];
        },
    },
    predefined_8: {
        get name () { return $t('rolesAndPermissions.aiAgentAdminName'); },
        get description () {
            return [
                $t('rolesAndPermissions.aiAgentAdminDescription1'),
                $t('rolesAndPermissions.aiAgentAdminDescription2'),
                $t('rolesAndPermissions.aiAgentAdminDescription3'),
            ];
        },
    },
    predefined_9: {
        get name () { return $t('rolesAndPermissions.replayUserName'); },
        get description () {
            return [
                $t('rolesAndPermissions.replayUserDescription1'),
                $t('rolesAndPermissions.replayUserDescription2'),
                $t('rolesAndPermissions.replayUserDescription3'),
                $t('rolesAndPermissions.replayUserDescription4'),
                $t('rolesAndPermissions.replayUserDescription5'),
            ];
        },
    },
    predefined_10: {
        get name () { return $t('rolesAndPermissions.journeyViewerName'); },
        get description () {
            return [
                $t('rolesAndPermissions.journeyViewerDescription1'),
                $t('rolesAndPermissions.journeyViewerDescription2'),
            ];
        },
    },
    predefined_11: {
        get name () { return $t('rolesAndPermissions.journeyContentEditorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.journeyContentEditorDescription1'),
                $t('rolesAndPermissions.journeyContentEditorDescription2'),
                $t('rolesAndPermissions.journeyContentEditorDescription3'),
            ];
        },
    },
    predefined_12: {
        get name () { return $t('rolesAndPermissions.journeyCreatorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.journeyCreatorDescription1'),
                $t('rolesAndPermissions.journeyCreatorDescription2'),
                $t('rolesAndPermissions.journeyCreatorDescription3'),
            ];
        },
    },
    predefined_13: {
        get name () { return $t('rolesAndPermissions.journeyPublisherName'); },
        get description () {
            return [
                $t('rolesAndPermissions.journeyPublisherDescription1'),
                $t('rolesAndPermissions.journeyPublisherDescription2'),
                $t('rolesAndPermissions.journeyPublisherDescription3'),
            ];
        },
    },
    predefined_14: {
        get name () { return $t('rolesAndPermissions.emailViewerName'); },
        get description () {
            return [
                $t('rolesAndPermissions.emailViewerDescription1'),
                $t('rolesAndPermissions.emailViewerDescription2'),
            ];
        },
    },
    predefined_15: {
        get name () { return $t('rolesAndPermissions.emailContentEditorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.emailContentEditorDescription1'),
                $t('rolesAndPermissions.emailContentEditorDescription2'),
            ];
        },
    },
    predefined_16: {
        get name () { return $t('rolesAndPermissions.emailCreatorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.emailCreatorDescription1'),
                $t('rolesAndPermissions.emailCreatorDescription2'),
            ];
        },
    },
    predefined_17: {
        get name () { return $t('rolesAndPermissions.emailPublisherName'); },
        get description () {
            return [
                $t('rolesAndPermissions.emailPublisherDescription1'),
                $t('rolesAndPermissions.emailPublisherDescription2'),
            ];
        },
    },
    voc_viewer_role: {
        get name () { return $t('rolesAndPermissions.vocViewerName'); },
        get description () { return $t('rolesAndPermissions.vocViewerDescription'); },
    },
    predefined_sub_6: {
        get name () { return $t('rolesAndPermissions.vocManagerName'); },
        get subHeading () { return $t('rolesAndPermissions.vocManagerSubHeading'); },
        get description () {
            return [
                $t('rolesAndPermissions.vocManagerDescription1'),
                $t('rolesAndPermissions.vocManagerDescription2'),
                $t('rolesAndPermissions.vocManagerDescription3'),
                $t('rolesAndPermissions.vocManagerDescription4'),
                $t('rolesAndPermissions.vocManagerDescription5'),
                $t('rolesAndPermissions.vocManagerDescription6'),
                $t('rolesAndPermissions.vocManagerDescription7'),
                $t('rolesAndPermissions.vocManagerDescription8'),
                $t('rolesAndPermissions.vocManagerDescription9'),
                $t('rolesAndPermissions.vocManagerDescription10'),
                $t('rolesAndPermissions.vocManagerDescription11'),
                $t('rolesAndPermissions.vocManagerDescription12'),
            ];
        },
    },
    predefined_sub_7: {
        get name () { return $t('rolesAndPermissions.vocContributorName'); },
        get subHeading () { return $t('rolesAndPermissions.vocContributorSubHeading'); },
        get description () {
            return [
                $t('rolesAndPermissions.vocContributorDescription1'),
                $t('rolesAndPermissions.vocContributorDescription2'),
                $t('rolesAndPermissions.vocContributorDescription3'),
                $t('rolesAndPermissions.vocContributorDescription4'),
                $t('rolesAndPermissions.vocContributorDescription5'),
            ];
        },
    },
    product_area_manager_sub_role: {
        name: 'Manage product areas',
        description: ['Create, edit, and organize subscription product areas used in analytics.'],
    },
    guide_theme_manager_sub_role: {
        name: 'Manage guide themes',
        description: ['Create, edit, and manage guide themes for this subscription.'],
    },
    command_center_view_sub_role: {
        get name () { return $t('rolesAndPermissions.commandCenterViewName'); },
        get description () {
            return [$t('rolesAndPermissions.commandCenterViewDescription1')];
        },
    },
    roadmap_viewer_sub_role: {
        get name () { return $t('rolesAndPermissions.roadmapViewerName'); },
        get description () { return $t('rolesAndPermissions.roadmapViewerDescription'); },
    },
    roadmap_creator_sub_role: {
        get name () { return $t('rolesAndPermissions.roadmapCreatorName'); },
        get subHeading () { return $t('rolesAndPermissions.roadmapCreatorSubHeading'); },
        get description () {
            return [
                $t('rolesAndPermissions.roadmapCreatorDescription1'),
                $t('rolesAndPermissions.roadmapCreatorDescription2'),
                $t('rolesAndPermissions.roadmapCreatorDescription3'),
                $t('rolesAndPermissions.roadmapCreatorDescription4'),
                $t('rolesAndPermissions.roadmapCreatorDescription5'),
            ];
        },
    },
    roadmap_publisher_sub_role: {
        get name () { return $t('rolesAndPermissions.roadmapPublisherName'); },
        get subHeading () { return $t('rolesAndPermissions.roadmapPublisherSubHeading'); },
        get description () {
            return [
                $t('rolesAndPermissions.roadmapPublisherDescription1'),
                $t('rolesAndPermissions.roadmapPublisherDescription2'),
                $t('rolesAndPermissions.roadmapPublisherDescription3'),
            ];
        },
    },
};

export const SENTIMENT_ROLE_MAP = {
    predefined_2: {
        get name () { return $t('rolesAndPermissions.sentimentGuidePublisherName'); },
        get description () {
            return [
                $t('rolesAndPermissions.sentimentGuidePublisherDescription1'),
                $t('rolesAndPermissions.sentimentGuidePublisherDescription2'),
            ];
        },
        get footnote () { return $t('rolesAndPermissions.sentimentGuidePublisherFootnote'); },
    },
    predefined_3: {
        get name () { return $t('rolesAndPermissions.sentimentGuideCreatorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.sentimentGuideCreatorDescription1'),
                $t('rolesAndPermissions.sentimentGuideCreatorDescription2'),
                $t('rolesAndPermissions.sentimentGuideCreatorDescription3'),
                $t('rolesAndPermissions.sentimentGuideCreatorDescription4'),
                { requiresNPSTriage: true, text: $t('rolesAndPermissions.sentimentGuideCreatorDescriptionTriage') },
            ];
        },
        get footnote () { return $t('rolesAndPermissions.sentimentGuideCreatorFootnote'); },
    },
    predefined_4: {
        get name () { return $t('rolesAndPermissions.sentimentTaggingEditorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.sentimentTaggingEditorDescription1'),
                $t('rolesAndPermissions.sentimentTaggingEditorDescription2'),
            ];
        },
    },
    predefined_5: {
        get name () { return $t('rolesAndPermissions.sentimentGuideContentEditorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.sentimentGuideContentEditorDescription1'),
                $t('rolesAndPermissions.sentimentGuideContentEditorDescription2'),
                $t('rolesAndPermissions.sentimentGuideContentEditorDescription3'),
                { requiresNPSTriage: true, text: $t('rolesAndPermissions.sentimentGuideContentEditorDescriptionTriage') },
            ];
        },
        get footnote () { return $t('rolesAndPermissions.sentimentGuideContentEditorFootnote'); },
    },
    predefined_6: {
        get name () { return $t('rolesAndPermissions.sentimentResourceCenterAuthorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.sentimentResourceCenterAuthorDescription1'),
                $t('rolesAndPermissions.sentimentResourceCenterAuthorDescription2'),
            ];
        },
    },
    predefined_7: {
        get name () { return $t('rolesAndPermissions.sentimentResourceCenterPublisherName'); },
        get description () {
            return [
                $t('rolesAndPermissions.sentimentResourceCenterPublisherDescription1'),
                $t('rolesAndPermissions.sentimentResourceCenterPublisherDescription2'),
            ];
        },
    },
};

export const NPS_ROLE_MAP = {
    predefined_2: {
        get name () { return $t('rolesAndPermissions.npsGuidePublisherName'); },
        get description () {
            return [
                $t('rolesAndPermissions.npsGuidePublisherDescription1'),
                $t('rolesAndPermissions.npsGuidePublisherDescription2'),
            ];
        },
        get footnote () { return $t('rolesAndPermissions.npsGuidePublisherFootnote'); },
    },
    predefined_3: {
        get name () { return $t('rolesAndPermissions.npsGuideCreatorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.npsGuideCreatorDescription1'),
                $t('rolesAndPermissions.npsGuideCreatorDescription2'),
                $t('rolesAndPermissions.npsGuideCreatorDescription3'),
                $t('rolesAndPermissions.npsGuideCreatorDescription4'),
                { requiresNPSTriage: true, text: $t('rolesAndPermissions.npsGuideCreatorDescriptionTriage') },
            ];
        },
        get footnote () { return $t('rolesAndPermissions.npsGuideCreatorFootnote'); },
    },
    predefined_4: {
        get name () { return $t('rolesAndPermissions.npsTaggingEditorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.npsTaggingEditorDescription1'),
                $t('rolesAndPermissions.npsTaggingEditorDescription2'),
            ];
        },
    },
    predefined_5: {
        get name () { return $t('rolesAndPermissions.npsGuideContentEditorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.npsGuideContentEditorDescription1'),
                $t('rolesAndPermissions.npsGuideContentEditorDescription2'),
                $t('rolesAndPermissions.npsGuideContentEditorDescription3'),
                { requiresNPSTriage: true, text: $t('rolesAndPermissions.npsGuideContentEditorDescriptionTriage') },
            ];
        },
        get footnote () { return $t('rolesAndPermissions.npsGuideContentEditorFootnote'); },
    },
    predefined_6: {
        get name () { return $t('rolesAndPermissions.npsResourceCenterAuthorName'); },
        get description () {
            return [
                $t('rolesAndPermissions.npsResourceCenterAuthorDescription1'),
                $t('rolesAndPermissions.npsResourceCenterAuthorDescription2'),
            ];
        },
    },
    predefined_7: {
        get name () { return $t('rolesAndPermissions.npsResourceCenterPublisherName'); },
        get description () {
            return [
                $t('rolesAndPermissions.npsResourceCenterPublisherDescription1'),
                $t('rolesAndPermissions.npsResourceCenterPublisherDescription2'),
            ];
        },
    },
};

export const SEGMENT_VERIFICATION_OPTION = {
    id: VERIFY_SEGMENTS_ROLE,
    get label () { return $t('rolesAndPermissions.segmentVerificationLabel'); },
    get helperText () { return $t('rolesAndPermissions.segmentVerificationHelperText'); },
    get tooltip () { return $t('rolesAndPermissions.segmentVerificationDisabledTooltip'); },
    disabled: true,
};

export const MANAGE_PRODUCT_AREAS_OPTION = {
    id: MANAGE_PRODUCT_AREAS_SUB_ROLE,
    label: 'Manage product areas',
    get helperText () { return $t('rolesAndPermissions.manageProductAreasHelperText'); },
};

export const MANAGE_GUIDE_THEMES_OPTION = {
    id: MANAGE_GUIDE_THEMES_SUB_ROLE,
    label: 'Manage guide themes',
    get helperText () { return $t('rolesAndPermissions.manageGuideThemesHelperText'); },
};

export const COMMAND_CENTER_VIEW_OPTION = {
    id: COMMAND_CENTER_VIEW_ROLE,
    get label () { return $t('rolesAndPermissions.commandCenterViewLabel'); },
    get helperText () { return $t('rolesAndPermissions.commandCenterViewHelperText'); },
};

export const MANAGE_INCLUDE_EXCLUDE_LISTS_OPTION = {
    id: MANAGE_INCLUDE_EXCLUDE_LISTS_SUB_ROLE,
    get label () { return $t('rolesAndPermissions.manageIncludeExcludeListsLabel'); },
    get helperText () { return $t('rolesAndPermissions.manageIncludeExcludeListsHelperText'); },
};

export const VIEW_INCLUDE_EXCLUDE_LISTS_OPTION = {
    id: VIEW_INCLUDE_EXCLUDE_LISTS_SUB_ROLE,
    get label () { return $t('rolesAndPermissions.viewIncludeExcludeListsLabel'); },
    get helperText () { return $t('rolesAndPermissions.viewIncludeExcludeListsHelperText'); },
    get tooltip () { return $t('rolesAndPermissions.viewIncludeExcludeListsIncludedInManage'); },
};

export const SHARE_OPTIONS = [
    {
        id: SHARE_SEGMENTS_ROLE,
        get label () { return $t('rolesAndPermissions.shareSegmentsLabel'); },
        get helperText () { return $t('rolesAndPermissions.shareSegmentsHelperText'); },
        childOptions: [SEGMENT_VERIFICATION_OPTION],
    },
    {
        id: SHARE_REPORTS_ROLE,
        get label () { return $t('rolesAndPermissions.shareReportsLabel'); },
        get helperText () { return $t('rolesAndPermissions.shareReportsHelperText'); },
    },
];

export const VOC_OPTIONS = [
    {
        id: VOC_VIEWER_ROLE,
        get description () { return $t('rolesAndPermissions.vocViewerOptionDescription'); },
        get name () { return $t('rolesAndPermissions.vocViewerOptionName'); },
        isDisabled: true,
        selectionState: 'checked',
    },
    {
        id: VOC_CONTRIBUTOR_ROLE,
        get description () { return $t('rolesAndPermissions.vocContributorOptionDescription'); },
        get name () { return $t('rolesAndPermissions.vocContributorOptionName'); },
        selectionState: 'checked',
    },
    {
        id: VOC_MANAGER_ROLE,
        get description () { return $t('rolesAndPermissions.vocManagerOptionDescription'); },
        get name () { return $t('rolesAndPermissions.vocManagerOptionName'); },
    },
];

export const ROADMAP_OPTIONS = [
    {
        id: ROADMAP_VIEWER_ROLE,
        get description () { return $t('rolesAndPermissions.roadmapViewerOptionDescription'); },
        get name () { return $t('rolesAndPermissions.roadmapViewerOptionName'); },
        isDisabled: true,
        selectionState: 'checked',
    },
    {
        id: ROADMAP_CREATOR_ROLE,
        get description () { return $t('rolesAndPermissions.roadmapCreatorOptionDescription'); },
        get name () { return $t('rolesAndPermissions.roadmapCreatorOptionName'); },
        selectionState: 'unchecked',
    },
    {
        id: ROADMAP_PUBLISHER_ROLE,
        get description () { return $t('rolesAndPermissions.roadmapPublisherOptionDescription'); },
        get name () { return $t('rolesAndPermissions.roadmapPublisherOptionName'); },
        selectionState: 'unchecked',
    },
];

export const SELECTION_STATE = {
    checked: 'checked',
    indeterminate: 'indeterminate',
    unchecked: 'unchecked',
};

// The roles in this array are not retrieved or sent to the backend
// take special care to filter these out prior to updates, or to add them to the BE results when retrieving roles
export const SUPPLEMENTAL_ROLES = [
    SUBSCRIPTION_USER_ROLE,
    SUPER_ROLE,
    OPS_ROLE,
    SUPPORT_ROLE,
    GLOBAL_ACCESS_ROLE, // check
    ADOPT_MANAGER_ROLE,
    VOC_VIEWER_ROLE,
    ROADMAP_VIEWER_ROLE,
];

// Helper function determines if a role is managed through the api
// ASSUME yes unless the role is one of the ones we list above
export function isRoleManagedByAPI (role) {
    return !SUPPLEMENTAL_ROLES.includes(role);
}

export function isAdoptSub (subscriptionConfig) {
    return subscriptionConfig.isAdopt || subscriptionConfig.isAEU;
}

export function isListenSubscriptionModuleActive (subscriptionConfig, isVocListenFreemiumActive = false) {
    return (
        subscriptionConfig?.isVOC &&
        !isAdoptSub(subscriptionConfig) &&
        !isVocListenFreemiumActive
    );
}

export function hasResourceCenterPermissionSet (role) {
    if (role.id === 'predefined_6' || role.id === 'predefined_7') {
        return true;
    }
    if (!role.permissionSets) {
        return false;
    }

    return role.permissionSets.some((set) => RESOURCE_CENTER_PERMISSION_SETS.includes(set));
}

export function buildRoleMapBy (predefinedRoles, prop) {
    return predefinedRoles.reduce(
        (roleNameMap, role) => ({
            [role.id]: prop ? role[prop] : role,
            ...roleNameMap,
        }),
        {},
    );
}

export function buildRoleMapWithRoles (roles) {
    const predefinedRoles = roles.filter(({ type }) => type === 'predefined');
    const customRoles = roles.filter(({ type, name }) => type === 'custom' && Boolean(name));

    return buildRoleMapBy([...customRoles, ...predefinedRoles], 'name');
}

export function buildRoleList (roleIdList, roleNameMap) {
    if (!roleIdList || roleIdList.length === 0) {
        return '';
    }

    const predefinedRoles = [];
    const saveCustomRoles = cloneDeep(roleIdList);

    roleIdList.forEach((role) => {
        if (role.includes('predefined')) {
            predefinedRoles.push(role);
            saveCustomRoles.splice(saveCustomRoles.indexOf(role), 1);
        }
    });

    predefinedRoles.sort();
    const sortedRoleIdList = [...predefinedRoles, ...saveCustomRoles];

    return sortedRoleIdList.reduce((roleNames, id) => {
        if (roleNameMap[id]) {
            roleNames.push(roleNameMap[id]);
        }

        return roleNames;
    }, []);
}

export function buildRoleListDisplayName (roleIdList, roleNameMap) {
    if (!roleIdList || roleIdList.length === 0) {
        return '';
    }

    return buildRoleList(roleIdList, roleNameMap).join(', ');
}

export function buildApplicationPermissionsMap (user, apps, canSelectViewer = false) {
    if (!isEmpty(user) && user.applicationRoles && !isEmpty(user.applicationRoles)) {
        // `user.applicationRoles` array will need to be filtered in the future to handle custom roles
        // role.type === `custom` || `predefined` exists to do so
        let hasDefaultRole = false;
        const applicationRoles = cloneDeep(user.applicationRoles);

        // build `{ appId: [ ...roleIds ] }`
        return apps.reduce((appMap, { id }) => {
            // empty array of the app doesn't exist in the list because new or other
            const applicationRolesByApp = applicationRoles[id] || [];

            // create roleId list and check if the user already has default role
            const roleIdList = applicationRolesByApp.map((role) => {
                const id = role.id || role;
                if (id === DEFAULT_APPLICATION_ROLE) {
                    hasDefaultRole = true;
                }

                return id;
            });

            if (!canSelectViewer && (isEmpty(applicationRolesByApp) || !hasDefaultRole)) {
                // add at the front
                roleIdList.unshift(DEFAULT_APPLICATION_ROLE);
            }

            return {
                [id]: roleIdList,
                ...appMap,
            };
        }, {});
    }

    return apps.reduce((appMap, { id }) => {
        const defaultRoles = [!canSelectViewer && DEFAULT_APPLICATION_ROLE].filter(Boolean);

        return {
            [id]: defaultRoles,
            ...appMap,
        };
    }, {});
}

export function getApplicationRolesBySub (subId, type = '') {
    return http.get(`/api/s/${subId}/applicationrole?type=${type}`).then((res) => res.data);
}

/**
 * These nested loops check each application role a user has in each application and removes any custom role that doesn't exist.
 * This only checks users (non-admins), since admins don't have custom roles
 */
export function removeNonExistentCustomRoles (userList, roleNameMap) {
    userList.forEach((user) => {
        if (user.applicationRoles && !user.flags?.admin) {
            for (const application of Object.values(user.applicationRoles)) {
                for (const [index, roleId] of application.entries()) {
                    if (!roleId.includes('predefined') && !Object.keys(roleNameMap).includes(roleId)) {
                        application.splice(index, 1);
                    }
                }
            }
        }
    });
}

export function isRoleDisabled (role, selectedApps, disableRCRoles = false, canSelectViewer = false, viewerSelected = false, isRemoveMode = false) {
    return (
        (role.id === DEFAULT_APPLICATION_ROLE && !canSelectViewer) ||
        (role.id !== DEFAULT_APPLICATION_ROLE && viewerSelected && isRemoveMode) ||
        selectedApps.length === 0 ||
        (disableRCRoles && hasResourceCenterPermissionSet(role)) ||
        (role.id === REPLAY_USER_ROLE && isRemoveMode && selectedApps.every((app) => !app.enableSessionReplay && !app.applicationFlags?.enableSessionReplay)) ||
        (role.id === REPLAY_USER_ROLE && !isRemoveMode && selectedApps.some((app) => !app.enableSessionReplay && !app.applicationFlags?.enableSessionReplay))
    );
}

export function canAssignApplicationRoleToApp (role, app, disableRCRoles = false) {
    if (!role?.id) {
        return true;
    }

    if (role.id === REPLAY_USER_ROLE) {
        return !!(app.enableSessionReplay || app.applicationFlags?.enableSessionReplay);
    }

    if (disableRCRoles && hasResourceCenterPermissionSet(role)) {
        return false;
    }

    if (hasResourceCenterPermissionSet(role)) {
        return !!app.resourceCenterAccess;
    }

    return true;
}

export function getRoleSelectionStateAndAppNames (
    selectedApps,
    role,
    applicationPermissionsMap,
    disableRCRoles = false,
    canSelectViewer = false,
) {
    const { id: roleId } = role;

    const isAssignedOnSelectedApps = selectedApps.some(
        (app) => applicationPermissionsMap[app.id]?.includes(roleId),
    );
    const keepViewerLocked = roleId === DEFAULT_APPLICATION_ROLE && !canSelectViewer;
    const isDisabled = isRoleDisabled(role, selectedApps, disableRCRoles, canSelectViewer) &&
        (!isAssignedOnSelectedApps || keepViewerLocked);

    const selectionStateAndAppNames = {
        selectionState: SELECTION_STATE.unchecked,
        isDisabled,
        appNames: '',
        appsWithoutRC: '',
        appsWithSessionReplay: '',
        numAppsWithSessionReplay: 0,
        numApps: 0,
    };

    if (selectedApps.length === 0) {
        return selectionStateAndAppNames;
    }

    const appNames = [];
    const appsWithoutRC = [];
    const appsWithSessionReplay = [];
    selectedApps.forEach((app) => {
        if (applicationPermissionsMap[app.id].indexOf(roleId) > -1) {
            // Hide the Replay User role in case Session Replay has been disabled
            if (app.enableSessionReplay || roleId !== REPLAY_USER_ROLE) {
                appNames.push(app.displayName);
            }
        }
        if (!app.resourceCenterAccess) {
            appsWithoutRC.push(app.displayName);
        }
        if (app.enableSessionReplay) {
            const truncatedDisplayName =
                app.displayName.length > 39 ? `${app.displayName.slice(0, 36)}...` : app.displayName;
            appsWithSessionReplay.push(truncatedDisplayName);
        }
    });

    const prepend = appsWithSessionReplay.length > 0 ? ':\n- ' : '';

    // turn appNames into a string for display
    selectionStateAndAppNames.appNames = appNames.join(', ');
    selectionStateAndAppNames.appsWithoutRC = appsWithoutRC.join(', ');
    selectionStateAndAppNames.numAppsWithSessionReplay = appsWithSessionReplay.length;
    selectionStateAndAppNames.appsWithSessionReplay = `${prepend}${
        appsWithSessionReplay.length > 7
            ? `${appsWithSessionReplay.slice(0, 6).join('\n- ')}\n+ ${appsWithSessionReplay.length - 6} more`
            : appsWithSessionReplay.join('\n- ')
    }`;
    selectionStateAndAppNames.numApps = selectedApps.length;
    const autoEnableViewer = roleId === DEFAULT_APPLICATION_ROLE && !canSelectViewer;
    if (autoEnableViewer || appNames.length === selectedApps.length) {
        selectionStateAndAppNames.selectionState = SELECTION_STATE.checked;
    } else if (appNames.length > 0) {
        selectionStateAndAppNames.selectionState = SELECTION_STATE.indeterminate;
    }

    return selectionStateAndAppNames;
}

export function getRoleDescriptionData (roleId, { customRolesMap = {}, isAdopt = false, isPendoFree = false } = {}) {
    switch (roleId) {
        case DEFAULT_APPLICATION_ROLE:
        case 'sunrise_default':
        case VOC_VIEWER_ROLE:
        case 'predefined_sub_6':
        case 'predefined_sub_7':
        case 'product_area_manager_sub_role':
        case 'guide_theme_manager_sub_role':
        case COMMAND_CENTER_VIEW_ROLE:
        case AI_AGENT_ADMIN_ROLE:
        case REPLAY_USER_ROLE:
        case JOURNEY_VIEWER_ROLE:
        case JOURNEY_CONTENT_EDITOR_ROLE:
        case JOURNEY_CREATOR_ROLE:
        case JOURNEY_PUBLISHER_ROLE:
        case EMAIL_VIEWER_ROLE:
        case EMAIL_CONTENT_EDITOR_ROLE:
        case EMAIL_CREATOR_ROLE:
        case EMAIL_PUBLISHER_ROLE:
            return ROLE_MAP[roleId];
        case 'predefined_2':
        case 'predefined_3':
        case TAGGING_EDITOR_ROLE:
        case 'predefined_5':
        case 'predefined_6':
        case 'predefined_7':
            if (isAdopt) {
                return ROLE_MAP[roleId];
            }
            if (isPendoFree) {
                return NPS_ROLE_MAP[roleId];
            }

            return SENTIMENT_ROLE_MAP[roleId];
        default:
            return customRolesMap[roleId] || ROLE_MAP.default;
    }
}

export function filterRoleDescription (description, hasNPSTriageFlag) {
    if (!Array.isArray(description)) {
        return description;
    }

    return description.reduce((acc, item) => {
        if (item && typeof item === 'object') {
            if (item.requiresNPSTriage && !hasNPSTriageFlag) {
                return acc;
            }
            acc.push(item.text);

            return acc;
        }
        acc.push(item);

        return acc;
    }, []);
}

export function shouldIncludeReplayRole (role, hasSessionReplayViewerAccess) {
    return role.id !== REPLAY_USER_ROLE || hasSessionReplayViewerAccess;
}

export function shouldIncludeRCRole (role, hasResourceCenter) {
    return hasResourceCenter || !hasResourceCenterPermissionSet(role);
}

export function shouldIncludeApplicationRole (roleOrId, {
    hasSessionReplayViewerAccess = false,
    hasOrchestrate = false,
    hasGuides = false,
    hasResourceCenter = false,
    canViewAiAgents = false,
    canViewTaggingEditor = true,
} = {}) {
    const role = typeof roleOrId === 'string' ? { id: roleOrId } : roleOrId;
    const roleId = role.id;

    if (HIDDEN_ROLES.includes(roleId)) {
        return false;
    }
    if (!shouldIncludeReplayRole(role, hasSessionReplayViewerAccess)) {
        return false;
    }
    if (roleId === AI_AGENT_ADMIN_ROLE && !canViewAiAgents) {
        return false;
    }
    if (roleId === TAGGING_EDITOR_ROLE && !canViewTaggingEditor) {
        return false;
    }
    if ((JOURNEY_ROLES.includes(roleId) || EMAIL_ROLES.includes(roleId)) && !hasOrchestrate) {
        return false;
    }
    if (
        (roleId === 'predefined_2' || roleId === 'predefined_3' || roleId === 'predefined_5') &&
        !hasGuides
    ) {
        return false;
    }
    if (!shouldIncludeRCRole(role, hasResourceCenter)) {
        return false;
    }

    return true;
}

export function collectAssignedApplicationRoleIdsFromUsers (userList = []) {
    const assignedRoleIds = new Set();

    userList.forEach((user) => {
        if (user.applicationRoles) {
            Object.values(user.applicationRoles).forEach((roleIds) => {
                roleIds.forEach((roleId) => {
                    const normalizedRoleId = normalizeRoleId(roleId);
                    if (normalizedRoleId) {
                        assignedRoleIds.add(normalizedRoleId);
                    }
                });
            });
        }
    });

    return assignedRoleIds;
}

export function collectAssignedSubscriptionRoleIdsFromUsers (userList = []) {
    const assignedRoleIds = new Set();

    userList.forEach((user) => {
        user.subscriptionRoles?.forEach((roleId) => {
            const normalizedRoleId = normalizeRoleId(roleId);
            if (normalizedRoleId) {
                assignedRoleIds.add(normalizedRoleId);
            }
        });
    });

    return assignedRoleIds;
}

export function collectAssignedRoleIdsFromUsers (userList = []) {
    const assignedRoleIds = collectAssignedSubscriptionRoleIdsFromUsers(userList);

    collectAssignedApplicationRoleIdsFromUsers(userList).forEach((roleId) => {
        assignedRoleIds.add(roleId);
    });

    return assignedRoleIds;
}

export function normalizeRoleId (roleOrId) {
    if (!roleOrId) {
        return null;
    }

    return typeof roleOrId === 'object' ? roleOrId.id : roleOrId;
}

export function buildEditorApplicationRolesList (roles = [], roleNameMap = {}, assignedRoleIds = new Set()) {
    const rolesById = new Map(roles.map((role) => [role.id, role]));

    assignedRoleIds.forEach((roleId) => {
        if (rolesById.has(roleId)) {
            return;
        }

        const name = roleNameMap[roleId];
        if (!name) {
            return;
        }

        rolesById.set(roleId, {
            id: roleId,
            name,
            type: roleId.includes('predefined') ? 'predefined' : 'custom',
        });
    });

    return [...rolesById.values()];
}

export function shouldIncludeRoleInUserFilter (roleOrId, applicationRoleFilterContext, assignedRoleIds) {
    const roleId = typeof roleOrId === 'string' ? roleOrId : roleOrId.id;

    if (assignedRoleIds.has(roleId)) {
        return true;
    }

    return shouldIncludeApplicationRole(roleOrId, applicationRoleFilterContext);
}

export function getAssignedRoleIdsForUserConfig (userConfig) {
    return collectAssignedRoleIdsFromUsers(userConfig ? [userConfig] : []);
}

export function getAssignedApplicationRoleIdsForUserConfig (userConfig) {
    return collectAssignedApplicationRoleIdsFromUsers(userConfig ? [userConfig] : []);
}

export function hasAnyAssignedRole (assignedRoleIds, roleIds) {
    return roleIds.some((roleId) => assignedRoleIds.has(roleId));
}

export function buildSubscriptionCategoryOptionsForEditor (options, {
    moduleActive = false,
    assignedRoleIds = new Set(),
} = {}) {
    const sourceOptions = moduleActive
        ? options
        : options.filter((option) => assignedRoleIds.has(option.id));

    return sourceOptions.map((option) => ({ ...option }));
}

function getAppsWithViewerAccess (appRoles, activeAppIds) {
    const appIdSet = new Set(activeAppIds.map(String));

    return Object.keys(appRoles || {})
        .filter((appId) => appIdSet.has(appId) && appRoles[appId]?.includes(DEFAULT_APPLICATION_ROLE))
        .sort();
}

export function calculateAppAccessPercentageEPs (activeAppIds, updatedUser, existingUser) {
    const totalApps = activeAppIds.length;

    if (totalApps === 0) return;
    if (updatedUser.subscriptionRoles.includes(SUBSCRIPTION_ADMIN_ROLE)) return;

    const updatedAppsWithViewer = getAppsWithViewerAccess(updatedUser.applicationRoles, activeAppIds);

    if (existingUser) {
        const existingAppsWithViewer = getAppsWithViewerAccess(existingUser.applicationRoles, activeAppIds);

        if (isEqual(existingAppsWithViewer, updatedAppsWithViewer)) return;
    }

    const accessPercent = parseFloat(((updatedAppsWithViewer.length / totalApps) * 100).toFixed(2));

    return {
        userAction: existingUser ? 'update' : 'invite',
        applicationAccessPercent: accessPercent,
        appsWithAccess: updatedAppsWithViewer.length,
        totalApps,
    };
}

export function calculateBulkAppAccessPercentageEPs (activeAppIds, successes) {
    const accessPercentages = successes
        .map(({ user }) => calculateAppAccessPercentageEPs(activeAppIds, user))
        .filter(Boolean);

    if (accessPercentages.length) {
        const applicationAccessRaw = accessPercentages.reduce(
            (acc, { applicationAccessPercent }) => acc + applicationAccessPercent, 0,
        ) / accessPercentages.length;

        const applicationAccessPercent = parseFloat(applicationAccessRaw.toFixed(2));

        return {
            numUsersUpdated: accessPercentages.length,
            applicationAccessPercent,
        };
    }
}
